import { useUserSettings } from "./useUserSettings";
import translations from "../assets/translations.json";
import { useLocalStorage } from "./useLocalStorage";
import { useLoginStatus } from "./useLoginStatus";
import { useEffect } from "react";
import { useRouterState } from "@tanstack/react-router";

type Translations = {
  [key: string]: {
    GERMAN: string;
    ENGLISH: string;
  };
};

export type AllowedLanguages = "GERMAN" | "ENGLISH";

const typedTranslations: Translations = translations;

export default function useTranslation() {
  const loginStatus = useLoginStatus();
  const isLoggedIn = loginStatus.data?.state === "logged in";
  const pathname = useRouterState({
    select: (state) => state.location.pathname,
  });
  const isAppRoute = pathname.startsWith("/app");
  const userSettings = useUserSettings({ enabled: isLoggedIn });
  const [localStorageLanguage, setLocalStorageLanguage] =
    useLocalStorage<AllowedLanguages>("local-language", "ENGLISH");

  useEffect(() => {
    if (
      isAppRoute &&
      userSettings.isSuccess &&
      userSettings.data.app_language !== localStorageLanguage
    ) {
      setLocalStorageLanguage(userSettings.data.app_language);
    }
  }, [
    isAppRoute,
    localStorageLanguage,
    setLocalStorageLanguage,
    userSettings.data?.app_language,
    userSettings.isSuccess,
  ]);

  const effectiveLanguage =
    isAppRoute && userSettings.isSuccess
      ? userSettings.data.app_language
      : localStorageLanguage;

  return {
    language: effectiveLanguage as AllowedLanguages,
    translate: function (
      translationKey: string,
      variables?: Record<string, string | number>,
    ): string {
      if (isLoggedIn && !userSettings.isSuccess) {
        return "";
      }
      const translationEntry = typedTranslations[translationKey];
      if (translationEntry) {
        const translation = translationEntry[effectiveLanguage];
        if (translation) {
          return applyVariables(translation, variables);
        }
        const fallback = translationEntry["ENGLISH"];
        if (fallback) {
          return applyVariables(fallback, variables);
        }
      }
      return translationKey;
    },
  };
}

function applyVariables(
  translation: string,
  variables?: Record<string, string | number>,
) {
  if (!variables) {
    return translation;
  }
  return translation.replaceAll(/{{(.*?)}}/g, (_match, token) => {
    const value = variables[token.trim()];
    return value !== undefined ? String(value) : "";
  });
}
